import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, XCircle, Play, Gauge, Music, Zap, Wrench, Save } from "lucide-react";
import { api } from "@/lib/api";

const OPTS = [
  ["bpm", "BPM / tempo", Gauge],
  ["key", "Musical key", Music],
  ["energy", "Energy", Zap],
  ["tech", "Technical (loudness, defects)", Wrench],
];

export const AnalysisPanel = ({ onOpenLibrary, onChanged }) => {
  const [ov, setOv] = useState(null);
  const [opts, setOpts] = useState(["bpm", "key", "energy", "tech"]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState("");
  const [thr, setThr] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/music/analysis/overview");
      setOv(r.data);
      setThr((p) => p || r.data.energy_thresholds);
      if (r.data.running_job) { setJob(r.data.running_job); watch(r.data.running_job.job_id); }
    } catch { toast.error("Couldn't load analysis overview"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const watch = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/music/analysis/jobs/${jobId}`);
        setJob(r.data);
        if (r.data.status !== "running") {
          clearInterval(pollRef.current);
          const s = r.data.summary || {};
          toast[r.data.status === "done" ? "success" : "info"](
            r.data.status === "done"
              ? `Analysis finished — ${s.analyzed} analyzed, ${s.skipped} cached, ${s.failed} failed`
              : "Analysis cancelled");
          setJob(null);
          load();
          onChanged?.();
        }
      } catch { clearInterval(pollRef.current); setJob(null); }
    }, 2000);
  };

  const start = async (missingOnly) => {
    setBusy("start");
    try {
      const r = await api.post("/music/analyze", { options: opts, missing_only: missingOnly, force: !missingOnly });
      toast.success(`Analyzing ${r.data.total} tracks — free local analysis, no credits used`);
      setJob({ job_id: r.data.job_id, progress: 0, total: r.data.total, status: "running", summary: {} });
      watch(r.data.job_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start analysis"); }
    setBusy("");
  };

  const cancel = async () => {
    try { await api.post(`/music/analysis/jobs/${job.job_id}/cancel`); toast.info("Cancelling after the current track…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const saveThr = async () => {
    setBusy("thr");
    try {
      await api.patch("/music/analysis/settings", { low_max: Number(thr.low_max), high_min: Number(thr.high_min) });
      toast.success("Energy thresholds saved for this station");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy("");
  };

  if (!ov) return <Loader2 size={16} className="animate-spin text-ink-muted" />;

  const stat = (label, value, cls, preset) => (
    <button key={label} onClick={() => preset && onOpenLibrary?.(preset)} disabled={!preset}
      data-testid={`ml-analysis-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`rounded-xl border border-line bg-bg-surface p-3 text-left transition ${preset ? "hover:border-brand-amber/50 cursor-pointer" : "cursor-default"}`}>
      <div className={`text-xl font-display font-bold ${cls}`}>{value}</div>
      <div className="type-card-meta-label text-ink-muted">{label}</div>
    </button>
  );

  return (
    <div data-testid="ml-analysis" className="space-y-4">
      <p className="type-body text-ink-muted">
        Measured locally with FFmpeg + librosa — <b>free, no credits</b>, originals never modified. BPM and key are
        estimates and get flagged when uncertain (live takes, tempo changes, sparse mixes). Manual values are never overwritten.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ml-analysis-stats">
        {stat("Analyzed", ov.analyzed, "text-emerald-300")}
        {stat("Awaiting analysis", ov.awaiting, "text-brand-amber")}
        {stat("Missing BPM", ov.missing_bpm, "text-ink-primary")}
        {stat("Missing key", ov.missing_key, "text-ink-primary")}
        {stat("Missing genre", ov.missing_genre, "text-ink-primary", { tab: "genres" })}
        {stat("Missing energy", ov.missing_energy, "text-ink-primary")}
        {stat("Needs review", ov.needs_review, ov.needs_review ? "text-rose-300" : "text-ink-primary")}
        {stat("Total tracks", ov.total, "text-ink-primary")}
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-4 space-y-3">
        <div className="type-section-heading text-ink-primary flex items-center gap-2">
          <Activity size={14} className="text-brand-cyan" /> Run analysis
        </div>
        <div className="flex flex-wrap gap-3">
          {OPTS.map(([id, label, Icon]) => (
            <label key={id} className="flex items-center gap-1.5 text-xs text-ink-secondary cursor-pointer">
              <input type="checkbox" checked={opts.includes(id)} data-testid={`ml-analysis-opt-${id}`}
                onChange={() => setOpts((p) => (p.includes(id) ? p.filter((o) => o !== id) : [...p, id]))}
                className="accent-amber-500" />
              <Icon size={12} className="text-ink-muted" /> {label}
            </label>
          ))}
        </div>
        {job ? (
          <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 px-3 py-2" data-testid="ml-analysis-job">
            <div className="flex items-center justify-between gap-2 text-xs font-mono text-cyan-300">
              <span className="flex items-center gap-2"><Loader2 size={12} className="animate-spin" />
                Analyzing {job.progress}/{job.total} · {job.summary?.analyzed ?? 0} done · {job.summary?.skipped ?? 0} cached · {job.summary?.failed ?? 0} failed
              </span>
              <button onClick={cancel} data-testid="ml-analysis-cancel"
                className="flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[10px] font-bold text-ink-secondary hover:text-rose-300">
                <XCircle size={11} /> Cancel
              </button>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-bg-elevated overflow-hidden">
              <div className="h-full bg-cyan-400 transition-all" style={{ width: `${(job.progress / Math.max(1, job.total)) * 100}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => start(true)} disabled={!opts.length || !!busy || !ov.awaiting} data-testid="ml-analysis-start-missing"
              title={ov.awaiting ? "" : "Everything is already analyzed"}
              className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-50">
              {busy === "start" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
              Analyze all unanalyzed ({ov.awaiting})
            </button>
            <span className="type-timestamp text-ink-muted self-center">
              …or select tracks in the Music Library tab and use “Analyze audio” there. Already-analyzed tracks are cached and skipped.
            </span>
          </div>
        )}
      </div>

      {thr && (
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="ml-analysis-thresholds">
          <div className="type-section-heading text-ink-primary mb-1 flex items-center gap-2">
            <Zap size={14} className="text-brand-amber" /> Station energy bands
          </div>
          <p className="type-timestamp text-ink-muted mb-2">
            Your format decides what counts as high energy — a hot Quiet Storm track is still mellow next to disco.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-ink-secondary">Low ≤
              <input type="number" min="1" max="98" value={thr.low_max} data-testid="ml-analysis-thr-low"
                onChange={(e) => setThr((p) => ({ ...p, low_max: e.target.value }))}
                className="ml-1.5 w-16 rounded-md border border-line bg-bg-base px-2 py-1 text-xs" />
            </label>
            <label className="text-xs text-ink-secondary">High ≥
              <input type="number" min="2" max="100" value={thr.high_min} data-testid="ml-analysis-thr-high"
                onChange={(e) => setThr((p) => ({ ...p, high_min: e.target.value }))}
                className="ml-1.5 w-16 rounded-md border border-line bg-bg-base px-2 py-1 text-xs" />
            </label>
            <span className="type-timestamp text-ink-muted">Medium is everything between.</span>
            <button onClick={saveThr} disabled={busy === "thr"} data-testid="ml-analysis-thr-save"
              className="type-button-label flex items-center gap-1 rounded-md border border-line px-3 py-1.5 text-ink-primary hover:bg-white/5 disabled:opacity-50">
              {busy === "thr" ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
